import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Activity, AlertOctagon, Timer } from 'lucide-react';
import { getVerzuim } from '../../services/dataCockpitApi';
import { usePeriode } from '../../store/dataPeriodeStore';
import { DataFilterBalk, PrivacyOnderdrukt, DataKop, Kaart, KpiTegel, Laden, Fout } from '../../components/data/DataFilterBalk';
import { TrendChart, Legenda, KLEUR, euro, pct } from '../../components/data/DataCharts';
import { VerzuimTrend } from '../../components/VerzuimTrend';
import { VerzuimIntelligence } from '../../components/VerzuimIntelligence';

/** Submap 1 — Verzuim- & Compliance-Meldingen. */
export const DataVerzuimPage: React.FC = () => {
  const { t } = useTranslation('employer');
  const { periode, key } = usePeriode();
  const [afdeling, setAfdeling] = useState('');
  const [correlatie, setCorrelatie] = useState(false);
  const q = useQuery({ queryKey: ['data-verzuim', ...key, afdeling], queryFn: () => getVerzuim(periode, afdeling) });
  const d = q.data;

  return (
    <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
      <DataKop titel={t('dataCockpit.verzuim.titel')} ondertitel={t('dataCockpit.verzuim.ondertitel')} icoon={<Activity className="h-6 w-6" />} />
      <DataFilterBalk />
      {q.isLoading && <Laden />}
      {q.isError && <Fout fout={q.error} />}
      {d && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            <KpiTegel label={t('dataCockpit.verzuim.kpiGemiddeld')} waarde={d.grendel.onderdrukt ? '🔒' : pct(d.gemiddeld_pct)} sub={t('dataCockpit.verzuim.kpiGemiddeldSub')} />
            <KpiTegel label={t('dataCockpit.verzuim.kpiZiekmeldingen')} waarde={d.nieuwe_ziekmeldingen ?? '🔒'} sub={t('dataCockpit.verzuim.kpiInPeriode')} />
            <KpiTegel
              label={t('dataCockpit.verzuim.kpiFte')}
              waarde={d.fte.fte == null ? '—' : d.fte.fte.toLocaleString('nl-NL')}
              sub={d.fte.bron ? t(`dataCockpit.verzuim.fteBron.${d.fte.bron}`) : t('dataCockpit.verzuim.fteOnbekend')}
            />
            <KpiTegel
              label={t('dataCockpit.verzuim.kpiRisico')}
              waarde={euro(d.risicometer.totaal)}
              accent={d.risicometer.totaal > 0 ? KLEUR.rood : undefined}
              sub={t('dataCockpit.verzuim.kpiRisicoSub', { n: d.risicometer.aantal })}
            />
          </div>

          {/* Widget A + C: trend, met correlatie-schakelaar */}
          <Kaart
            titel={t('dataCockpit.verzuim.widgetA')}
            uitleg={t('dataCockpit.verzuim.widgetAUitleg')}
            actie={
              <div className="flex flex-wrap items-center gap-3">
                {!d.scope_vast && (
                  <select
                    aria-label={t('dataCockpit.verzuim.afdelingFilter')}
                    className="rounded-lg border border-gray-300 px-2 py-1.5 text-sm"
                    value={afdeling}
                    onChange={(e) => setAfdeling(e.target.value)}
                  >
                    <option value="">{t('dataCockpit.verzuim.heleOrganisatie')}</option>
                    {d.afdelingen.map((a) => <option key={a.id} value={a.id}>{a.naam}</option>)}
                  </select>
                )}
                <label className={`flex items-center gap-2 text-sm ${d.correlatie_beschikbaar ? 'text-gray-700' : 'text-gray-400'}`}>
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-gray-300"
                    checked={correlatie && d.correlatie_beschikbaar}
                    disabled={!d.correlatie_beschikbaar}
                    onChange={(e) => setCorrelatie(e.target.checked)}
                  />
                  {t('dataCockpit.verzuim.toonCorrelatie')}
                </label>
              </div>
            }
          >
            {d.grendel.onderdrukt ? (
              <PrivacyOnderdrukt grendel={d.grendel} />
            ) : d.trend.every((m) => m.verzuim_pct == null) ? (
              <p className="py-10 text-center text-sm text-gray-500">
                {t('dataCockpit.verzuim.geenFte')}{' '}
                <Link to="/employer/mijn-belagom?tab=flexbudget" className="font-medium text-[#0B1F3A] underline">{t('dataCockpit.verzuim.naarPayroll')}</Link>
              </p>
            ) : (
              <>
                <TrendChart
                  maanden={d.trend.map((m) => m.maand)}
                  lijn={d.trend.map((m) => m.verzuim_pct)}
                  lijnLabel={t('dataCockpit.verzuim.verzuimPct')}
                  staven={correlatie && d.correlatie_beschikbaar ? d.trend.map((m) => m.medewerker_meldingen) : undefined}
                  stavenLabel={t('dataCockpit.verzuim.meldingen')}
                />
                {correlatie && d.correlatie_beschikbaar && (
                  <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
                    <Legenda items={[{ label: t('dataCockpit.verzuim.legendaLijn'), kleur: KLEUR.rood }, { label: t('dataCockpit.verzuim.legendaStaven'), kleur: KLEUR.blauw }]} />
                    <p className="text-xs text-gray-500">{t('dataCockpit.verzuim.correlatieUitleg')}</p>
                  </div>
                )}
                <p className="mt-2 text-xs text-gray-400">
                  {d.trend.some((m) => m.bron === 'payroll') ? t('dataCockpit.verzuim.bronPayroll') : t('dataCockpit.verzuim.bronZiekmeldingen')}
                </p>
              </>
            )}
            {!d.correlatie_beschikbaar && !d.grendel.onderdrukt && (
              <p className="mt-2 text-xs text-gray-500">{t('dataCockpit.verzuim.correlatieGeblokkeerd')}</p>
            )}
          </Kaart>

          <div className="grid gap-6 lg:grid-cols-3">
            {/* Widget B: loonsanctie-risicometer */}
            <Kaart titel={t('dataCockpit.verzuim.widgetB')} uitleg={t('dataCockpit.verzuim.widgetBUitleg')} className="lg:col-span-2">
              <div className="mb-4 flex flex-wrap items-center gap-4">
                <div className={`flex items-center gap-3 rounded-lg px-4 py-3 ${d.risicometer.aantal ? 'bg-red-50' : 'bg-green-50'}`}>
                  <AlertOctagon className={`h-6 w-6 ${d.risicometer.aantal ? 'text-red-600' : 'text-green-600'}`} aria-hidden />
                  <div>
                    <p className="text-xs text-gray-600">{t('dataCockpit.verzuim.risicoTotaal')}</p>
                    <p className={`text-xl font-bold ${d.risicometer.aantal ? 'text-red-700' : 'text-green-700'}`}>{euro(d.risicometer.totaal)}</p>
                  </div>
                </div>
                <p className="text-sm text-gray-600">
                  {t(`dataCockpit.verzuim.segment.${d.risicometer.segment}`, {
                    bedrag: euro(d.risicometer.bedrag_per_overtreding),
                    grens: d.risicometer.enterprise_grens,
                  })}
                </p>
              </div>
              {d.risicometer.overtredingen.length === 0 ? (
                <p className="text-sm text-green-700">✓ {t('dataCockpit.verzuim.geenOvertredingen')}</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200 text-left text-xs text-gray-500">
                        <th className="py-2 pr-4 font-medium">{t('dataCockpit.verzuim.kolCase')}</th>
                        <th className="py-2 pr-4 font-medium">{t('dataCockpit.verzuim.kolMijlpaal')}</th>
                        <th className="py-2 pr-4 font-medium">{t('dataCockpit.verzuim.kolDeadline')}</th>
                        <th className="py-2 pr-4 font-medium">{t('dataCockpit.verzuim.kolOverTijd')}</th>
                        <th className="py-2 text-right font-medium">{t('dataCockpit.verzuim.kolSanctie')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {d.risicometer.overtredingen.map((o) => (
                        <tr key={o.case_code} className="border-b border-gray-100">
                          <td className="py-2 pr-4 font-mono text-xs">{o.case_code}</td>
                          <td className="py-2 pr-4">{o.mijlpaal}</td>
                          <td className="py-2 pr-4">{o.deadline ? new Date(`${o.deadline}T00:00:00`).toLocaleDateString('nl-NL') : '—'}</td>
                          <td className="py-2 pr-4 text-red-700">{t('dataCockpit.verzuim.dagen', { n: o.dagen_over_tijd ?? 0 })}</td>
                          <td className="py-2 text-right font-semibold text-red-700">🔴 {euro(d.risicometer.bedrag_per_overtreding)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <Link to="/employer/trajecten/lopend" className="mt-3 inline-block text-sm font-medium text-[#0B1F3A] underline">
                    {t('dataCockpit.verzuim.naarPoortwachter')}
                  </Link>
                </div>
              )}
            </Kaart>

            {/* Widget D: doorlooptijd week 6 */}
            <Kaart titel={t('dataCockpit.verzuim.widgetD')} uitleg={t('dataCockpit.verzuim.widgetDUitleg')}>
              {d.doorlooptijd.grendel.onderdrukt ? (
                <PrivacyOnderdrukt grendel={d.doorlooptijd.grendel} compact />
              ) : (
                <div className="flex items-center gap-4">
                  <Timer className="h-10 w-10 text-[#0B1F3A]" aria-hidden />
                  <div>
                    <p className={`text-3xl font-bold ${d.doorlooptijd.gem_dagen != null && d.doorlooptijd.gem_dagen > d.doorlooptijd.wettelijke_termijn_dagen ? 'text-red-700' : 'text-gray-900'}`}>
                      {d.doorlooptijd.gem_dagen == null ? '—' : t('dataCockpit.verzuim.dagen', { n: d.doorlooptijd.gem_dagen.toLocaleString('nl-NL') })}
                    </p>
                    <p className="text-xs text-gray-500">
                      {t('dataCockpit.verzuim.doorlooptijdSub', { n: d.doorlooptijd.aantal ?? 0, termijn: d.doorlooptijd.wettelijke_termijn_dagen })}
                    </p>
                  </div>
                </div>
              )}
            </Kaart>
          </div>
        </div>
      )}

      {/* Samengevoegd uit de oude /employer/insights: CBS-verzuimtrend uit de Payroll Bridge
          en Verzuim-Intelligence per vestiging/afdeling (eigen endpoints en privacy-grendels). */}
      <div className="mt-6 space-y-6">
        <VerzuimTrend />
        <VerzuimIntelligence />
      </div>
    </div>
  );
};

export default DataVerzuimPage;
